import React, { useState, useEffect, useContext } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Switch, Route, useRouteMatch, useHistory } from "react-router-dom";
import moment from "moment";
import Select from "../components/common/select";
import SelectDate from "../components/common/selectDate";
import ListGroup from "../components/common/listGroup";
import ScreenSelect from "../components/dataEntrySelect";
import DataEntryTable from "../components/dataentryTable";
import http from "../services/http";
import Loading from "../components/common/loading";
import UserContext from "../context/userContext";
import SidebarContext from "../context/sidebarContext";
import Joi from "joi";
import { calculateCCFBadges, calculateCCFBadge } from "../utils/CCFUtils";
import { isEmpty, isBlank, isPageReload } from "../utils/common/utils";

const DataEntry = (props) => {
  const [user] = useContext(UserContext);
  const [sidebar] = useContext(SidebarContext);
  const [parameters, setParameters] = useState([]);
  const [screens, setScreens] = useState([]);
  const [favouriteScreens, setFavouriteScreens] = useState([]);
  const [operations, setOperations] = useState([]);
  const [activeScreen, setActiveScreen] = useState("");
  const [operation, setOperation] = useState("");
  const [operationStart, setOperationStart] = useState("");
  const [operationEnd, setOperationEnd] = useState("");
  const [screenDate, setScreenDate] = useState(moment());
  const [screenHour, setScreenHour] = useState(moment().hour());
  const [screenType, setScreenType] = useState("");
  const [hourSelection, setHourSelection] = useState({
    id: "NOW",
    name: "Current Hour",
  });
  const [fontSelection, setFontSelection] = useState({
    id: "md",
    name: "Regular",
  });

  const [refresh, setRefresh] = useState(false);
  const [screenHeadings, setScreenHeadings] = useState([]);
  const [screenHeadingsOriginal, setScreenHeadingsOriginal] = useState([]);
  const [screenData, setScreenData] = useState([]);
  const [screenDataOriginal, setScreenDataOriginal] = useState([]);
  const [submissionList, setSubmissionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCodes, setShowCodes] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  let { path } = useRouteMatch();
  const history = useHistory();

  const hourChoice = [
    { id: "NOW", name: "Current Hour" },
    { id: "END", name: "Full Day Ending" },
  ];

  const fontSize = [
    { id: "md", name: "Regular" },
    { id: "sm", name: "Small" },
  ];

  useEffect(() => {
    const size = localStorage.getItem("ftr-de-size");
    if (size) setFontSelection(JSON.parse(size));

    const hourOption = localStorage.getItem("ftr-hour-selection");
    if (hourOption && (hourOption === "NOW" || hourOption === "END")) setHourSelection(hourChoice.find(choice => choice.id === hourOption));
  }, []);
  
  useEffect(() => {
    async function getFavourites() {
      try {
        const { data } = await http.get(`/data-screens/favourites`);
        setFavouriteScreens(data);
      }
      catch (ex) {
        toast.error("An error occurred while fetching favourites data");
      }
    }

    async function getOperations() {
      try {
        const { data } = await http.get(`/operations/active`);
        setOperations(data);
        if (data.length > 0) {
          const dataRow = isBlank(operation) ? data[0] : data.find(op => op.CODE === operation);

          setOperation(dataRow.CODE);
          setOperationStart(moment(dataRow.START_DATE));
          setOperationEnd(moment(dataRow.END_DATE));
        }
      }
      catch (ex) {
        toast.error("An error occurred while fetching operations data");
      }
    }
  
    if (user) {
      getFavourites();
      getOperations();
    }
  }, [user]);

  useEffect(() => {
    async function getParameters() {
      try {
        const { data } = await http.get("/ccf-parameters/list");
        setParameters(data);
      }
      catch {
        //ignore error
      }
    }

    async function getScreens() {
      try {
        const { data } = await http.get(`/data-screens`);
        setScreens(data);
      }
      catch (ex) {
        toast.error("An error occurred while fetching screens data");
      }
    }
    
    getParameters();
    getScreens();
  }, [user.SITE]);

  useEffect(() => {
    const elements = window.location.pathname.split('/');
    const newScreen = parseInt(elements[2]);
    const newOperation = elements[3];
    const newScreenType = elements[4];
    var newScreenDate = moment(elements[5]);
    const newHourSelection = elements[6];

    if (isBlank(newScreen) || isBlank(newOperation) || isBlank(newScreenType) || !newScreenDate) return;

    let updateScreen = false;

    if (newScreen !== activeScreen) {
      setActiveScreen(newScreen);  
      updateScreen = true;
    }

    if (!newScreenDate.isSame(screenDate, "day")) {
      setScreenDate(newScreenDate);
      updateScreen = true;
    }

    if (newScreenType !== screenType) {
      setScreenType(newScreenType);
      updateScreen = true;
    }

    if (newOperation !== operation) {
      setOperation(newOperation);
      updateScreen = true;
    }

    var newHour = newHourSelection === "NOW" ? moment().hour() : 6;

    if (newHourSelection !== hourSelection.id) {
      const selection = hourChoice.find(choice => choice.id === newHourSelection);
      setHourSelection(selection);
      setScreenHour(Number(newHour));
      updateScreen = true;
    }

    if (updateScreen) selectScreen(newScreen, newOperation, newScreenType, newScreenDate, newHour);
  }, [user, refresh]);

  async function getHeadingData(screen, operation, screenType, screenDate, screenHour) {
    if (isBlank(screen) || isBlank(operation) || isBlank(screenType) || isBlank(screenDate)) return [];

    try {
      const apiDate = screenDate ? screenDate.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
      const { data } = await http.get(`/data-screen/heading/${screen}/${operation}/${screenType}/${apiDate}/${screenHour}`);

      setScreenHeadings(data);
      setScreenHeadingsOriginal(data.slice());
      return data;
    }
    catch (ex) {
      toast.error("An error occurred while retrieving heading data.");
      return [];
    }
  }

  async function getScreenData(headings, newScreen, newOperation, newScreenType, newScreenDate, newScreenHour, showLoading) {
    if (isEmpty(headings) || isBlank(newScreenDate) || isBlank(newScreen) ||isBlank(newOperation)) return;

    if (showLoading) setLoading(true);
      try {
        const apiDate = newScreenDate.format("YYYY-MM-DD");
        const apiEndPoint =
          newScreenType === "HLY"
            ? `/data-screen/hourly-data-ds/${newScreen}/${newOperation}/${apiDate}/${newScreenHour}`
            : `/data-screen/daily-data/${newScreen}/${newOperation}/${apiDate}`;
  
        const { data: newScreenData } = await http.get(apiEndPoint);
        if (!isEmpty(newScreenData)) {
          const originalHeadings = !isEmpty(screenHeadingsOriginal) ? screenHeadingsOriginal : headings.slice();
          const newHeadings = calculateCCFBadges(headings, originalHeadings, newScreenData, newScreenData, null);
          setScreenHeadings(newHeadings);
          setScreenData(mapScreenData(newScreenData));
          setScreenDataOriginal(newScreenData.slice());
        }
      }
      catch (ex) {
        toast.error("An error occurred while retrieving screen data");
    }
  }

  function mapScreenData(data) {
    return data.map(row => {
      const newRow = {};

      for (const key of Object.keys(row)) {
        if (key === 'FTR_DATE' || 'FTR_HOUR' || 'REAL_DATE' || 'REAL_HOUR' || row[key] === null) {
          newRow[key] = row[key];
        }
        else {
          newRow[key] = row[key];
        }
      }

      return newRow;
    });
  }

  async function selectScreen(newScreen, newOperation, newScreenType, newScreenDate, newScreenHour) {
    if (isNaN(newScreen) || isBlank(newScreen) || isBlank(newOperation) || isBlank(newScreenType) || isBlank(newScreenDate)) return;

    try {
      if (newScreen !== activeScreen) {
      setLoading(true);

      setSubmissionList([]);
      
      const headings = await getHeadingData(newScreen, newOperation, newScreenType, newScreenDate, newScreenHour);
      if (!isEmpty(headings)) {
        await getScreenData(headings, newScreen, newOperation, newScreenType, newScreenDate, newScreenHour, true);
      }

      http.post(`/data-screen-log/${newScreen}`);

      setLoading(false);
    }
    else {
      await getScreenData(screenHeadings, newScreen, newOperation, newScreenType, newScreenDate, newScreenHour, false);
    }
    }
    catch (ex) {
      if (ex.message) toast.error(ex.message);
    }
  }

  async function handleScreenChange({ currentTarget: input }) {
    const newScreen = Number(input.value);
    const newScreenType = screens.find(s => s.ID === newScreen).SCREEN_TYPE;

    history.push(`${path}/${newScreen}/${operation}/${newScreenType}/${screenDate.format("YYYY-MM-DD")}/${getHourSelection().id}`);

    setRefresh(!refresh);
  }

  function handleOperationChange({ currentTarget: input }) {
    const newOperation = operations.find((o) => o.CODE === input.value);

    setSubmissionList([]);
    setOperationStart(moment(newOperation.START_DATE));
    setOperationEnd(moment(newOperation.END_DATE));

    history.push(`${path}/${activeScreen}/${newOperation.CODE}/${screenType}/${screenDate.format("YYYY-MM-DD")}/${getHourSelection().id}`);

    setRefresh(!refresh);
  }

  function handleDateChange({ currentTarget: input }) {
    const newScreenDate = moment(input.value);

    history.push(`${path}/${activeScreen}/${operation}/${screenType}/${newScreenDate.format("YYYY-MM-DD")}/${getHourSelection().id}`);

    setRefresh(!refresh);
  }

  function handleHourChange(item) {
    setSubmissionList([]);

    history.push(`${path}/${activeScreen}/${operation}/${screenType}/${screenDate.format("YYYY-MM-DD")}/${item.id}`);
    localStorage.setItem("ftr-hour-selection", item.id);
    setRefresh(!refresh);
  }

  function handleDataChange({ currentTarget: input }, data, parameter, dataIndex) {
    if (parameter.TYPE_ID === "SIM") return;

    const newScreenData = [...screenData];
    let newSubmissionList = [...submissionList];

    const index = newSubmissionList.findIndex(
      (s) =>
        s.CODE === parameter.CODE &&
        s.FTR_DATE === data.FTR_DATE &&
        s.FTR_HOUR === data.FTR_HOUR
    );

    const originalValue = screenDataOriginal[dataIndex][parameter.CODE];

    const regex = /[^0-9.-]/g;
    const value = input.value
      .replace(regex, ``)
      .replace("..", ".")
      .replace("--", "-");

      // Check Absolute Limits
    const valid = value === "" || (
      (parameter.UPPER_ABSOLUTE === null || Number(value) <= parameter.UPPER_ABSOLUTE) &&
      (parameter.LOWER_ABSOLUTE === null || Number(value) >= parameter.LOWER_ABSOLUTE));

    if (index === -1) { // New submissionList entry
      if (IsBlank(screenData[dataIndex][parameter.CODE])) { // No previous value, insert new value
        newSubmissionList.push({
          TYPE: "Insert",
          CODE: parameter.CODE,
          FTR_DATE: data.FTR_DATE,
          FTR_HOUR: data.FTR_HOUR,
          OPERATION: operation,
          AUDIT: parameter.AUDIT_FLAG,
          VALUE: value,
          ORIGINAL_VALUE: originalValue,
          VALID: valid
        });
      }
      else if (screenData[dataIndex][parameter.CODE] !== null && value === "") { // Delete previous value
        newSubmissionList.push({
          TYPE: "Delete",
          CODE: parameter.CODE,
          FTR_DATE: data.FTR_DATE,
          FTR_HOUR: data.FTR_HOUR,
          OPERATION: operation,
          AUDIT: parameter.AUDIT_FLAG,
          VALUE: value,
          ORIGINAL_VALUE: originalValue,
          VALID: true
        });
      }
      else { // Update previous value to new value
        newSubmissionList.push({
          TYPE: "Update",
          CODE: parameter.CODE,
          FTR_DATE: data.FTR_DATE,
          FTR_HOUR: data.FTR_HOUR,
          OPERATION: operation,
          AUDIT: parameter.AUDIT_FLAG,
          VALUE: value,
          ORIGINAL_VALUE: originalValue,
          VALID: valid
        });
      }
    }
    else { //Alter existing submissionList entry
      newSubmissionList[index].VALUE = value;
      newSubmissionList[index].VALID = valid;

      if (value !== "" && newSubmissionList[index].TYPE === "Delete") { // Alter submissionList entry to Update
        newSubmissionList[index].TYPE = "Update";
      }

      if (value === "" && (newSubmissionList[index].ORIGINAL_VALUE === null || newSubmissionList[index].ORIGINAL_VALUE === "")) { // Remove this insert from submissionList
        newSubmissionList.splice(index, 1);
      }
      else if (value === newSubmissionList[index].ORIGINAL_VALUE) { // Remove this update or delete from submissionList
        newSubmissionList.splice(index, 1);
      }
      else if (value === "" && (newSubmissionList[index].ORIGINAL_VALUE !== null || newSubmissionList[index].ORIGINAL_VALUE !== "")) { // Alter submissionList entry to Delete
        newSubmissionList[index].TYPE = "Delete";
        newSubmissionList[index].VALUE = "";
      }
    }

    newScreenData[dataIndex][parameter.CODE] = value;

    if (screenDate.isSame(moment(), "day") || screenDate.isSame(moment().add(1, "day"), "day")) {
      const newHeadings = calculateCCFBadges(screenHeadings, screenHeadingsOriginal, newScreenData, screenDataOriginal, null);
      setScreenHeadings(newHeadings);
    }

    setSubmissionList(newSubmissionList);
    setScreenData(newScreenData);
  }

  const schema = Joi.object({
    AUDIT: Joi.boolean().required(),
    CODE: Joi.string().required(),
    FTR_DATE: Joi.date().required(),
    FTR_HOUR: Joi.number().min(1).max(24).required(),
    OPERATION: Joi.string().required(),
    ORIGINAL_VALUE: Joi.number().allow(null, "").required(),
    TYPE: Joi.string().required(),
    VALUE: Joi.number().allow(null, "").required(),
    VALID: Joi.boolean().deny(false)
  });//.messages({'boolean.valid': 'One or more values are outside absolute limits'});

  function handleCheckSubmissions() {
    if (submissionList.length === 0) return;
    const errorList = submissionList.filter((sub) => {
      const errors = validate(sub, schema);
      if (errors) console.log(errors);
      return !!errors;
    });
    errorList.map((err) => {
      return toast.error(`Error on code: ${err.CODE}`);
    });
    if (errorList.length === 0) toast.info("No errors found");
  }

  function validate(entry, schema) {
    const options = { abortEarly: false };
    const { error } = schema.validate(entry, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) errors[item.path[0]] = item.message;
    return errors;
  }

  async function displayCCFModal() {
    let newCCF = 0;
    let pendingCloseCCF = 0;

    const { data } = await http.get(`/data-screen/heading/${activeScreen}/${operation}/${screenType}/${screenDate}/${screenHour}`);

    var message = [];
    message.push("The status of these parameters will change:\r\n\r\n");

    screenHeadings.forEach (heading => {
      switch (heading.CCFLABEL) {
        case "New CCF":
          if (!heading.CCFS) {
            message.push(heading.CODE + " (New CCF)");
            newCCF++;
          }
          break;
        case "Pending Close":
          if (data[data.findIndex(h => h.CODE === heading.CODE)].CCFLABEL !== "Pending Close") {
            message.push(heading.CODE + " (Pending Close)");
            pendingCloseCCF++;
          }
          break;
        default:
          //do nothing
          break;
      }
    });

    if (newCCF > 0 || pendingCloseCCF > 0) {
      message.push( "\r\n\r\nDo you want to proceed?");

      setModalMessage(message);
      setShowModal(true);
    }
    else {
      handleSubmit();
    }
  }
  
  const handleClose = (result) => {
    switch (result) {
      case "Yes":
        setShowModal(false);  
        handleSubmit();
        break;
      case "No":
        setShowModal(false);
        break;
      default:
        //do nothing
        break;
    }
  }

  async function handleSubmit() {
    const updateSidebar = props ? props.updateSidebar : undefined;

    if (submissionList.length === 0) {
      return toast.info("Nothing to Submit");
    }
    // Validate
    const errorList = submissionList.filter((sub) => {
      const errors = validate(sub, schema);
      if (errors) console.log(errors);
      return !!errors
    });
    errorList.map((err) => {
      return toast.error(`Error on code: ${err.CODE}`);
    });
    if (errorList.length !== 0) return;

    // Submit data
    setSubmitting(true);
  
    try {
      //Remove the VALID property before sending to the API
      //as it isn't expecting it
      submissionList.forEach(v=>delete v.VALID);

      await http.post(`/data`, { data: submissionList });
    } catch (ex) {
      setSubmitting(false);
      return toast.error("Data submission failed");
    }

    try {
      await http.post(`/data/recalculate/${operation}`);
    } catch (ex) {
      toast.error("Data recalculation failed");
    }

    addCCFs(screenHeadings, screenData);

    setSubmitting(false);
    updateSidebar();

    // Erase List
    setSubmissionList([]);

    // Refresh Screen
    updateCCF(null);

    screenHeadings.map((heading) => {
      if (heading.CCFLABEL === "New CCF") heading.CCFLABEL = "Ongoing CCF";
    });
    setScreenHeadings(screenHeadings);
    setScreenHeadingsOriginal(screenHeadings.slice());
    // Setting screenhour based on the selection either Current Hour or Fullday ending
    var screenHour = getHourSelection().id === "NOW" ? moment().hour() : 6;
    setScreenHour(Number(screenHour));
    await getScreenData(screenHeadings, activeScreen, operation, screenType, screenDate, screenHour, false);
    
    toast.success("Data Submitted");
  }

  function handleSizeChange(font) {
    setFontSelection(font);
    localStorage.setItem("ftr-de-size", JSON.stringify(font));
  }

  const handleAddToFav = async () => {
    if (!activeScreen) return;
    try {
      const { data } = await http.post(`/data-screen-favourites/${activeScreen}`);
      setFavouriteScreens((s) => [...s, data]);
      toast.success("Added to favourites");
    } catch (error) {
      if (error.response && error.response.status < 500) {
        toast.error(error.response.data);
      }
    }
  };

  function isNotFavourite() {
    if (!activeScreen) return false;
    if (favouriteScreens.length === 0) return true;
    const index = favouriteScreens.findIndex(
      (s) => s.ID === parseInt(activeScreen)
    );
    if (index === -1) return true;
    return false;
  }

  function updateCCF(columnIndex) {
    const updatedHeadings = calculateCCFBadges(screenHeadings, screenHeadingsOriginal, screenData, screenDataOriginal, columnIndex);
    setScreenHeadings(updatedHeadings);
  }
  
  async function addCCFs(headingData, screenData) {
    if (isEmpty(headingData) || isEmpty(screenData)) return;

    headingData.forEach( async heading => {
      if (heading.CCF !== null) {
        const parameter = heading.CODE;

        switch (calculateCCFBadge(heading, screenHeadingsOriginal, screenData, screenDataOriginal)) {
          case "New CCF":
            let ccfType = '';
            let ccfAnalysis = '';
            const status = 'O'; //data entry CCFs are always set to Open
            const raisedFrom = 'DATAENTRY'; //OPERATOR = from CCFList screen, DATAENTRY = from Data Entry screens

            try {
              var param = parameters.find(({ CODE }) => CODE === parameter);
              if (param) {
                ccfType = param.CCF_TYPE;
                ccfAnalysis = param.CCF_ANALYSIS;

                const { data } = await http.get(`/ccf/${param.CODE}/${operation}/O`);

                if (data && data.length === 0) {
                  http.post("/ccf", { parameter, ccfType, ccfAnalysis, operation, status, raisedFrom });
                }
              }
            }
            catch(error) {
              if (error.response && error.response.status < 500) {
                toast.error(error.response.data);
              }
            }
            break;
          case "Ongoing CCF":
            //no action required
            break;
          case "Pending Close":
            //find existing CCF and change status to Pending Close
            try {
              const { data } = await http.get(`/ccf/${parameter}/${operation}/O`);

              if (data) {
                const now = moment().format("YYYY-MM-DD HH:mm:ss");
                await http.post(`/ccf/ccfend/${data[0].ID}/${now}/R`);
              }
            }
            catch(error) {
              if (error.response && error.response.status < 500) {
                toast.error(error.response.data);
              }
            }
            break;
          default:
            //do nothing
            break;
        }
      }
    });
  }

  function IsBlank(value) {
    return (value === '' || value === null || value === undefined);
  }

  function getHourSelection() {
    if (hourSelection) return hourSelection;

    const hourOption = localStorage.getItem("ftr-hour-selection");
    if (hourOption) return hourChoice.find(choice => choice.id === hourOption);
    
    return hourChoice[0];
  }

  return (  
    // <div className="col-10 mt-4" onKeyDown={handleKeyDown} tabIndex="0">
    <React.Fragment>
      <div className={sidebar ? "col-10 mt-4" : "col-12 mt-4"}>
        <div className="row">
          <div className="col">
            <ScreenSelect
              name="screen"
              placeholder="Data Screen"
              value={activeScreen}
              allOptions={screens}
              favourites={favouriteScreens}
              onChange={handleScreenChange}
            />
          </div>
          <div className="col">
            <Select
              name="operation"
              placeholder="Operation"
              value={operation}
              options={operations}
              onChange={handleOperationChange}
              listKey="CODE"
              listName="COMMENTS"
            />
          </div>
          <div className="col">
            <SelectDate
              value={screenDate.format("YYYY-MM-DD")}
              onChange={handleDateChange}
            />
          </div>
          <div className="col">
            <button
              className="btn btn-success"
              onClick={displayCCFModal}
              disabled={submissionList.length === 0 || submitting}
            >
              {submitting ? (
                <div
                  className="spinner-border spinner-border-sm text-white mr-1"
                  role="status"
                ></div>
              ) : (
                <i className="fas fa-check mr-1"></i>
              )}
              Submit
            </button>
            <button
              className="btn btn-primary ml-1"
              onClick={() => setShowOptions(!showOptions)}
            >
              <i className="fas fa-cog mr-1"></i>
              {showOptions ? (
                <i className="fas fa-caret-up"></i>
              ) : (
                <i className="fas fa-caret-down"></i>
              )}
            </button>
            {isNotFavourite() && (
              <button
                className="btn btn-warning ml-1"
                onClick={handleAddToFav}>
                  <i className="fas fa-star" />
              </button>
            )}
          </div>
        </div>
        {showOptions && (
          <div className="row">
            <div className="col-3">
              {screenType === "HLY" && (
                <ListGroup
                  items={hourChoice}
                  onItemSelect={handleHourChange}
                  selectedItem={hourSelection}
                />
              )}
            </div>
            <div className="col-3">
              <button
                className="btn btn-primary mb-3"
                onClick={() => setShowCodes(!showCodes)}
              >
                {showCodes ? "Hide Codes" : "Show Codes"}
              </button>
              <button
                className="btn btn-success mb-3 ml-1"
                onClick={handleCheckSubmissions}
                disabled={submissionList.length === 0 || submitting}
              >
                <i className="fas fa-bug"></i> Check
              </button>
            </div>
            <div className="col-3">
              <ListGroup
                items={fontSize}
                onItemSelect={(font) => handleSizeChange(font)}
                selectedItem={fontSelection}
              />
              {/* {activeScreen && (
              <ListGroup
                items={displayChoice}
                onItemSelect={item => setDisplaySelection(item)}
                selectedItem={displaySelection}
              />
            )} */}
            </div>
          </div>
        )}
        <div className="row px-2">
          {loading && <Loading />}
          <Switch>
            <Route path={`${path}/:screenId`}>
              {activeScreen && !loading && (
                <DataEntryTable
                  id="dataEntry"
                  headings={screenHeadings}
                  parameterData={screenData}
                  originalData={screenDataOriginal}
                  handleChange={handleDataChange}
                  updateSidebar={props.updateSidebar}
                  updateCCF={updateCCF}
                  apiDate = {screenDate.format("YYYY-MM-DD")}
                  startDate={operationStart}
                  endDate={operationEnd}
                  user={user}
                  type={screenType}
                  showCodes={showCodes}
                  displayRows={24}
                  handleSubmit={displayCCFModal}
                  fontSize={fontSelection.id}
                />
              )}
            </Route>
          </Switch>
        </div>
      </div>
      <CCFModal
        show={showModal}
        handleCcfClose={handleClose}
        message={modalMessage}
      />
    </React.Fragment>
  );
};

const CCFModal = ({ show, handleCcfClose, message }) => {
  return (
    <Modal show={show} size="md" onHide={() => handleCcfClose("No")}>
      <Modal.Header className="bg-warning">
        <h3>Critical Control Form</h3>
      </Modal.Header>
      <Modal.Body>
        <h5 className="modalWrap">{message}</h5>
        <br></br>
        <br></br>
        <div className="row">
          <div className="col">
            <button
              className="btn btn-lg btn-success"
              onClick={() => handleCcfClose("Yes")}
            >
              Yes
            </button>
          </div>
          <div className="col">
            <button
              className="btn btn-lg btn-danger"
              onClick={() => handleCcfClose("No")}
            >
              No
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DataEntry;
